/**
 * Game State Core - Pure Data Structure
 *
 * Defines the core game state structure without any browser/Alpine dependencies.
 * This is the pure data structure that can be used in both browser and Node.js.
 * UI updates and Alpine reactivity are handled in gameState.js.
 */

import * as config from '../constants.js';
import { getRandomAvatar } from './util.js';
import { GAME_VERSION } from '../version.js';
import { debug, isDebug, CH } from './debugLog.js';

/**
 * Default state for the Command prestige layer (M2). Lives here (with the save
 * shape) so command-core.js can import it without a module cycle.
 * - tour: account-global tour counter (current tour number, increments on promotion)
 * - legends: immutable snapshots of promoted characters
 * - upgrades: upgradeId -> ranks owned (defs in js/data/commandUpgrades.js)
 * - charterRegions: regions unlocked via Regional Charter picks (account-wide)
 * - vault: Guild Vault material carryover awaiting the next created character
 * - grandfathered: pre-Command save - region locks are not enforced (all regions)
 * - revealed: Command tab has been revealed (one-way, set at unlockThreshold)
 */
// Save-FORMAT version - the migration gate (stable; distinct from the auto
// build number in gameVersion). BUMP THIS whenever you change the persisted
// SHAPE of gameState/character/command (add/rename/remove/restructure a saved
// field), and add the matching migration in loadGameStateCore gated on the old
// version. It is intentionally manual: only structural changes bump it, so
// loaders and fixtures can reason about format cleanly.
// v2 = the Command-era overhaul (skills, items, progression redesigned).
export const SAVE_SCHEMA_VERSION = 3;

/**
 * Detect a pre-overhaul ("legacy") save that cannot be faithfully migrated: the
 * Command-era redesign renamed essentially every skill id, so any skill id a
 * character carries that is NOT a current skill is stranded, dead data that will
 * crash the skill/raid systems downstream. Pure (caller passes the current
 * skill-id set) so it stays testable and core-safe.
 *
 * A save is legacy if ANY skill-bearing character references ANY unknown skill
 * id. It is deliberately "any", not "all": one surviving skill (e.g.
 * system-override was the sole id that kept its name) leaves a character with a
 * mix of current and stale ids - that character still crashes, so it must still
 * reset. A genuinely fresh/post-reset save only ever contains current ids, so
 * this cannot false-positive. Returns false only for saves with no stale skill
 * data at all (fresh/empty, or fully on current ids).
 */
export function isLegacySave(parsed, knownSkillIds) {
    const known = knownSkillIds instanceof Set ? knownSkillIds : new Set(knownSkillIds || []);
    const chars = [
        ...((parsed?.gameState?.characters) || []),
        parsed?.gameState?.character,
    ].filter(c => c && c.skills && Object.keys(c.skills).length > 0);
    if (chars.length === 0) return false; // no skill data to strand
    return chars.some(c => Object.keys(c.skills).some(id => !known.has(id)));
}

/**
 * Default the per-character fields introduced by the Command-era systems so a
 * save made mid-overhaul (already on current skill ids, but before a given
 * field existed) loads without crashing. Readers of synergyUnlocks/avatarSeed
 * are already null-safe; equippedTechniqueGear is written unguarded, so it must
 * exist.
 */
/**
 * A character's skills with EVERY faction skill present, 0 where unspent.
 *
 * The invariant the UI reads by. The skill tab strip, the gear matrix and the
 * consumables pane all enumerate the FACTION's five skills, so a character
 * carrying only the ones they have spent points on renders a tab whose card is
 * blank — which is exactly what partial fixtures produced. Guaranteeing the
 * shape once here is cheaper, and less fragile, than making every reader
 * defensive about a key that should always exist.
 *
 * An unknown faction leaves the data alone: better a character that renders
 * oddly than one whose levels this quietly blanks.
 */
export function withAllFactionSkills(faction, skills) {
    const factionSkills = config.factionSkills[faction]?.skills;
    if (!factionSkills) return { ...(skills || {}) };
    const filled = {};
    for (const skillId of Object.keys(factionSkills)) filled[skillId] = skills?.[skillId] ?? 0;
    return filled;
}

export function ensureCharacterFields(character) {
    if (!character) return;
    if (!character.equippedTechniqueGear) character.equippedTechniqueGear = {};
    if (!character.techniqueTabSelections) character.techniqueTabSelections = {};
    if (!character.synergyUnlocks) character.synergyUnlocks = {};
    character.skills = withAllFactionSkills(character.faction, character.skills);
}

export function defaultCommandState() {
    return {
        marks: 0,
        tour: 1,
        legends: [],
        upgrades: {},
        charterRegions: [],
        vault: {},
        unlockedArtifacts: [], // account-level: designs unlocked (blueprint consumed), for the one-time unlock marks
        grandfathered: false,
        revealed: false
    };
}

/**
 * The Syndicate layer (M3) - the prestige above Command.
 *
 * Split from `command` rather than nested inside it because founding WIPES the
 * command branch wholesale (docs/layers/3-syndicate/design.md §2). Anything that must
 * outlive a founding has to live outside the thing being reset, so the
 * boundary between these two objects is the reset contract itself.
 *
 * `eraMarks` is the Writ input: marks banked since the last founding, which is
 * NOT `command.marks` (that is a spendable balance and goes down when you buy
 * something). Writs must price the era you ran, not the change left in your
 * pocket at the end of it.
 */
export function defaultSyndicateState() {
    return {
        writs: 0,
        founded: 0,          // foundings completed; gates The Breach (L4) later
        eraMarks: 0,         // marks BANKED this era - the Writ input, not a balance
        totalTours: 0,       // lore clock: tours accumulated across all eras
        upgrades: {},        // charter ranks (mirrors command.upgrades)
        leader: null,        // Syndicate Leader: Legend snapshot, permanent ops anchor
        pastLeaders: [],     // one archived per founding
        operations: [],
        opsCompleted: 0,
        // The Guild Vault's two operations-side stores. Both live here rather
        // than on `command` because founding REBUILDS that branch from defaults;
        // same reason `eraMarks` and `succession` sit here.
        //
        // consignment — what this Command era has banked for the NEXT founding.
        //   Every promotion splits the outgoing character's materials:
        //   progressionScaling.vaultCarryover keeps a share for the next recruit
        //   (command.vault) and the remainder lands here instead of being
        //   destroyed. Folded into `stores` at founding, then cleared.
        //
        // stores — the founded Syndicate's dock, and the ONLY thing convoys
        //   load from. Filled at founding and spent down over the era; the
        //   current era's promotions feed `consignment`, not this, because that
        //   era is building the next Syndicate, not resupplying this one.
        //
        // Two fields, not one: founding is repeatable. Collapsing them would
        // make every promotion silently top up the live dock and delete the
        // per-era budget entirely.
        //
        // Account-level on purpose. Cargo used to draw from the ACTIVE
        // character's inventory, which meant switching character — or being
        // auto-promoted mid-plan — could invalidate a planned operation and
        // would have broken Operations automation outright.
        consignment: {},
        stores: {},
        // Market saturation: "regionId:faction" -> { level, at }. How much of
        // each region's appetite for each faction's goods is already spent, and
        // when that was last measured — demand-core decays it on read rather
        // than ticking it. Survives founding on purpose (see satiationLevel):
        // it is a fact about the world's markets, not about an era.
        demand: {},
        blackout: { cleared: {} }, // contractId -> true; perks derived from this
        // Succession Doctrine bookkeeping for the CURRENT Command tour. Lives
        // here rather than on `command` because founding wipes that branch and
        // this is Syndicate machinery; it is reset per tour at promotion.
        succession: {
            tourRaids: 0,     // raids completed by the current character
            peakRate: 0,      // best marks-per-raid average seen this tour
            peakAtRaid: 0,    // tourRaids when that peak was set
            paused: false,    // player override; the charter stays owned
            template: null,   // { faction, skillPlan } for the successor
        },
        revealed: false
    };
}

export function ensureLifetimeCounters(character) {
    if (character && !character.lifetime) {
        character.lifetime = { t3Wins: 0, artifactsCrafted: 0, artifactsUnlocked: 0 };
    }
    // Backfill the field on pre-existing saves/characters
    if (character?.lifetime && character.lifetime.artifactsUnlocked == null) {
        character.lifetime.artifactsUnlocked = 0;
    }
    return character?.lifetime;
}

// Game state - global game data only, character-specific data lives in character objects
export const gameState = {
    schemaVersion: SAVE_SCHEMA_VERSION, // stable save-FORMAT version - the migration gate (bump on structure changes)
    gameVersion: GAME_VERSION,          // auto build version that last wrote the save - provenance/debugging only
    player: {
        credits: 1000,
        energy: 100,
        maxEnergy: 100,
        lastEnergyUpdate: Date.now(),
        achievements: {},
        // Collapse Keys recovered, by id. ACCOUNT-LEVEL ON PURPOSE: the player
        // object is saved and restored whole and is never rebuilt by a
        // promotion or a founding, so the story survives every prestige without
        // an entry in any reset ledger. A wipe that erased the story would be
        // indefensible, and the cheapest way to guarantee that is to put the
        // record somewhere the wipes cannot reach.
        // See js/modules/collapseKeys-core.js.
        collapseKeys: []
    },
    character: null, // Active character object (has skillPoints, skills, inventory, artifacts, equippedArtifacts)
    characters: [], // Array of all player characters
    activeCharacterId: null, // ID of currently active character
    contractors: [],
    selectedFaction: null,
    currentRaid: {
        currentRaidGrid: null,
        selectedPath: [],
        selectedRegion: null,
        selectedTier: null,
        // {row, col} on the world map — WHERE in the region this raid is
        // happening. Rolled with the grid, read by the world clock (M3 S9).
        // BELONGS TO THE GRID: meaningless without `currentRaidGrid`, and
        // readers must check that rather than trust the field alone. Two
        // separate teardown paths null the grid (resetRaidCore and main.js's
        // performRaidCancel, which duplicates it inline), so a rule that has to
        // be re-stated in each of them is a rule that will be missed in the
        // third one.
        raidSite: null,
        raidInProgress: null,
        raidCompleted: false,
        completedRaidInfo: null,
        raidOwnerId: null,
        hiredContractors: [],
        reservedContractors: []
    },
    command: defaultCommandState(),
    syndicate: defaultSyndicateState()
};

/**
 * The authoritative live copy of a character.
 *
 * The ACTIVE character exists twice: as `gameState.character` (the live working
 * copy the UI and game logic mutate) and as an entry in `gameState.characters`.
 * saveGameStateCore below merges `gameState.character` OVER the roster entry on
 * every save, so any write made only to the roster entry of the active character
 * is silently discarded on the next save. Always route writes (XP, tokens,
 * marketplace flags) through this helper.
 *
 * Returns `gameState.character` for the active character, otherwise the roster
 * entry unchanged. Non-active characters have exactly one copy, so this is a
 * no-op for them.
 */
export function liveCharacter(character) {
    if (!character) return character;
    return (character.id === gameState.activeCharacterId && gameState.character)
        ? gameState.character
        : character;
}

export function saveGameStateCore() {
    // Save current character's inventory, artifacts, and equipped artifacts before saving
    //console.log('Current character:', gameState.character);
    if (gameState.character && gameState.activeCharacterId) {
        const currentCharIndex = gameState.characters.findIndex(c => c.id === gameState.activeCharacterId);
        if (currentCharIndex !== -1) {
            gameState.characters[currentCharIndex] = {
                ...gameState.characters[currentCharIndex],
                ...gameState.character
            };
        }
        debug(CH.sim, 'Saved character:', gameState.characters[currentCharIndex]);
    }        

    const saveData = {
        version: '1.0',
        timestamp: Date.now(),
        gameState: {
            schemaVersion: SAVE_SCHEMA_VERSION, // save-format version (migration gate)
            gameVersion: GAME_VERSION,          // build that wrote this save (provenance)
            player: gameState.player,
            character: gameState.character, // Includes skillPoints, skills, inventory, artifacts, equippedArtifacts
            characters: gameState.characters || [], // All characters with their own data
            activeCharacterId: gameState.activeCharacterId,
            selectedFaction: gameState.selectedFaction,
            contractors: gameState.contractors,
            currentRaid: gameState.currentRaid,
            settings: gameState.settings || {},
            command: gameState.command || defaultCommandState(),
            syndicate: gameState.syndicate || defaultSyndicateState()
        }
    };

    return saveData;
}

/**
 * Generic load function - works in both browser and Node.js
 * @param {string} key - Storage key
 * @returns {Object|null} Loaded data or null if not found
 */
export function loadGameStateCore(parsed) {
    debug(CH.sim, 'Loading data:', parsed);

    // Schema version the save was written at (absent → predates the stamp, treat
    // as 1). The field-level migrations below are additive/idempotent; gate any
    // future version-specific migration on savedVersion, then the state is
    // stamped to the current version at the end of this function.
    const savedVersion = parsed?.gameState?.schemaVersion ?? 1;
    const savedGameVersion = parsed?.gameState?.gameVersion; // which build wrote it (provenance)

    // Restore game state - handle both new player object format and legacy format
    if (parsed.gameState.player) {
        gameState.player = parsed.gameState.player;
    } else {
        // Migrate legacy format
        gameState.player = {
            credits: parsed.gameState.credits !== undefined ? parsed.gameState.credits : 1000,
            energy: parsed.gameState.energy !== undefined ? parsed.gameState.energy : 100,
            maxEnergy: parsed.gameState.maxEnergy || 100,
            lastEnergyUpdate: parsed.gameState.lastEnergyUpdate || Date.now(),
            achievements: parsed.gameState.achievements || {}
        };
    }

    // Restore currentRaid - handle both new format and legacy format
    if (parsed.gameState.currentRaid) {
        gameState.currentRaid = parsed.gameState.currentRaid;
    } else {
        // Migrate legacy format
        gameState.currentRaid = {
            currentRaidGrid: parsed.gameState.currentRaidGrid || null,
            selectedPath: parsed.gameState.selectedPath || [],
            selectedRegion: parsed.gameState.selectedRegion || null,
            selectedTier: parsed.gameState.selectedTier || null,
            raidInProgress: parsed.gameState.raidInProgress || null,
            raidCompleted: parsed.gameState.raidCompleted || false,
            completedRaidInfo: parsed.gameState.completedRaidInfo || null,
            raidOwnerId: parsed.gameState.raidOwnerId || null,
            hiredContractors: parsed.gameState.hiredContractors || [],
            reservedContractors: parsed.gameState.reservedContractors || []
        };
    }

    gameState.character = parsed.gameState.character || null;
    gameState.characters = parsed.gameState.characters || [];
    gameState.activeCharacterId = parsed.gameState.activeCharacterId || null;

    // Command layer (M2): restore or initialize. Saves from before the Command
    // layer keep unrestricted region access (grandfathered) so nobody loses
    // regions mid-campaign; region locks apply to fresh accounts and onward.
    if (parsed.gameState.command) {
        gameState.command = { ...defaultCommandState(), ...parsed.gameState.command };
    } else {
        gameState.command = defaultCommandState();
        if (gameState.characters.length > 0) {
            gameState.command.grandfathered = true;
        }
    }

    // Syndicate layer (M3): purely additive, so v2 saves need no migration code -
    // they simply shallow-merge onto the defaults and arrive un-revealed with
    // zero Writs, which is exactly the state a pre-Syndicate account is in.
    gameState.syndicate = { ...defaultSyndicateState(), ...(parsed.gameState.syndicate || {}) };

    // Lifetime counters (feed the Command Marks formula) - additive migration
    gameState.characters.forEach(ensureLifetimeCounters);
    ensureLifetimeCounters(gameState.character);

    // Command-era per-character fields (skill gear / synergies): default them so
    // a save from mid-overhaul loads cleanly.
    gameState.characters.forEach(ensureCharacterFields);
    ensureCharacterFields(gameState.character);

    // Ensure HP properties are synchronized for character
    if (gameState.character) {
        // If currentHp is missing, use hp as fallback (for legacy saves)
        if (gameState.character.currentHp === undefined && gameState.character.hp !== undefined) {
            gameState.character.currentHp = gameState.character.hp;
        }

        // Ensure character has a name (for legacy saves)
        if (!gameState.character.name) {
            gameState.character.name = 'You';
        }

        // If currentHp is still missing, default to maxHp
        if (gameState.character.currentHp === undefined) {
            gameState.character.currentHp = gameState.character.maxHp || 100;
        }
        
        // Add isAlive and injuries properties if missing
        if (gameState.character.isAlive === undefined) {
            gameState.character.isAlive = true;
        }
        
        if (!gameState.character.injuries) {
            gameState.character.injuries = [];
        }            
    }

    gameState.selectedFaction = parsed.gameState.selectedFaction || null;
    // Migrate legacy top-level skillPoints/skills to character objects
    if (parsed.gameState.skillPoints !== undefined || parsed.gameState.skills) {
        console.log('Migrating legacy skillPoints/skills to character objects');
        const legacySkillPoints = parsed.gameState.skillPoints !== undefined ? parsed.gameState.skillPoints : 15;
        const legacySkills = parsed.gameState.skills || {};

        gameState.characters.forEach(character => {
            if (character.skillPoints === undefined) {
                character.skillPoints = legacySkillPoints;
            }
            if (!character.skills) {
                character.skills = { ...legacySkills };
            }
        });
    }

    // Handle inventory migration and loading
    const hasLegacyInventory = parsed.gameState.inventory || parsed.gameState.artifacts || parsed.gameState.equippedArtifacts;

    if (hasLegacyInventory && gameState.characters.length > 0) {
        // Legacy save file - migrate shared inventory to character-specific inventories
        console.log('Migrating shared inventory to character-specific inventories');
        const legacyInventory = parsed.gameState.inventory || {};
        const legacyArtifacts = parsed.gameState.artifacts || [];
        const legacyEquippedArtifacts = parsed.gameState.equippedArtifacts || { skillSlot: null, utilitySlot: null };

        gameState.characters.forEach(character => {
            if (!character.inventory) {
                // Give each character a copy of the shared inventory
                character.inventory = { ...legacyInventory };
                character.artifacts = [...legacyArtifacts];
                character.equippedArtifacts = { ...legacyEquippedArtifacts };
            }
        });

        // Legacy data already migrated to character objects above
    } else if (gameState.characters.length > 0) {
        // Modern save - ensure all characters have inventory/artifacts initialized
        gameState.characters.forEach(character => {
            if (!character.inventory) character.inventory = {};
            if (!character.artifacts) character.artifacts = [];
            if (!character.equippedArtifacts) {
                character.equippedArtifacts = { skillSlot: null, utilitySlot: null };
            }
        });
    }

    // Deduplicate contractors and remove player contractors (they don't belong in the pool)
    const contractorMap = new Map();
    (parsed.gameState.contractors || [])
        .filter(c => c.id < 0) // Only keep NPC contractors (negative IDs)
        .forEach(c => contractorMap.set(c.id, c));
    gameState.contractors = Array.from(contractorMap.values());


    // Migrate old contractor data: convert cost to rewardPercentage and add avatars
    if (gameState.contractors && gameState.contractors.length > 0) {
        gameState.contractors.forEach(contractor => {
            if (typeof contractor.rewardPercentage === 'undefined' && typeof contractor.cost !== 'undefined') {
                // Convert old fixed cost to percentage (10-25% range)
                contractor.rewardPercentage = 10 + Math.floor(Math.random() * 16);
                delete contractor.cost; // Remove old property
            }
            // Add avatar if missing
            if (!contractor.avatar) {
                contractor.avatar = getRandomAvatar(contractor.faction);
            }
            // Backfill procedural avatar seed for old saves. Seed from the
            // already-varied legacy sprite string so faces are stable and distinct
            // without needing a per-contractor id (the id is a reused slot index).
            if (!contractor.avatarSeed) {
                contractor.avatarSeed = contractor.avatar;
            }
            // Add isAlive and injuries properties if missing
            if (contractor.isAlive === undefined) {
                contractor.isAlive = true;
            }
            
            if (!contractor.injuries) {
                contractor.injuries = [];
            }
        });
    }

    // Also migrate reserved contractors
    if (gameState.currentRaid.reservedContractors && gameState.currentRaid.reservedContractors.length > 0) {
        gameState.currentRaid.reservedContractors.forEach(contractor => {
            if (typeof contractor.rewardPercentage === 'undefined' && typeof contractor.cost !== 'undefined') {
                contractor.rewardPercentage = 10 + Math.floor(Math.random() * 16);
                delete contractor.cost;
            }
            // Add avatar if missing
            if (!contractor.avatar) {
                contractor.avatar = getRandomAvatar(contractor.faction);
            }
            // Backfill procedural avatar seed for old saves. Seed from the
            // already-varied legacy sprite string so faces are stable and distinct
            // without needing a per-contractor id (the id is a reused slot index).
            if (!contractor.avatarSeed) {
                contractor.avatarSeed = contractor.avatar;
            }

            // Add isAlive and injuries properties if missing
            if (contractor.isAlive === undefined) {
                contractor.isAlive = true;
            }
            
            if (!contractor.injuries) {
                contractor.injuries = [];
            }
        });
    }

    // Also migrate hired contractors
    if (gameState.currentRaid.hiredContractors && gameState.currentRaid.hiredContractors.length > 0) {
        gameState.currentRaid.hiredContractors.forEach(contractor => {
            if (typeof contractor.rewardPercentage === 'undefined' && typeof contractor.cost !== 'undefined') {
                contractor.rewardPercentage = 10 + Math.floor(Math.random() * 16);
                delete contractor.cost;
            }
            // Add avatar if missing
            if (!contractor.avatar) {
                contractor.avatar = getRandomAvatar(contractor.faction);
            }
            // Backfill procedural avatar seed for old saves. Seed from the
            // already-varied legacy sprite string so faces are stable and distinct
            // without needing a per-contractor id (the id is a reused slot index).
            if (!contractor.avatarSeed) {
                contractor.avatarSeed = contractor.avatar;
            }

            // Add isAlive and injuries properties if missing
            if (contractor.isAlive === undefined) {
                contractor.isAlive = true;
            }

            if (!contractor.injuries) {
                contractor.injuries = [];
            }
        });
    }

    gameState.settings = parsed.gameState.settings || {};
    // NOTE: `unlockedRegions` was persisted here until 2026-07-28 but gated
    // nothing — region availability is DERIVED (command-core.js
    // availableRegionsForCharacter: faction starting pair + charterRegions).
    // It is no longer read or written; old saves simply drop the field on their
    // next save, so no migration is needed.
    gameState.equippedArtifacts = parsed.gameState.equippedArtifacts || { skillSlot: null, utilitySlot: null };

    // Veteran's Stipend was replaced by Field Conditioning (skill points ->
    // max HP). Carry the owned rank across so a player who bought it keeps the
    // slot they paid marks for; command.upgrades is account-permanent and
    // marks are not refundable, so dropping the id would silently confiscate it.
    const upgrades = gameState.command?.upgrades;
    if (upgrades && upgrades['veterans-stipend'] != null) {
        if (upgrades['field-conditioning'] == null) {
            upgrades['field-conditioning'] = upgrades['veterans-stipend'];
        }
        delete upgrades['veterans-stipend'];
        debug(CH.ui, 'Migrated veterans-stipend rank to field-conditioning');
    }

    // Migrate old generalSlot to utilitySlot in equipped artifacts
    if (gameState.equippedArtifacts.generalSlot && !gameState.equippedArtifacts.utilitySlot) {
        gameState.equippedArtifacts.utilitySlot = gameState.equippedArtifacts.generalSlot;
        delete gameState.equippedArtifacts.generalSlot;
        console.log('Migrated artifact from generalSlot to utilitySlot');
    } else if (gameState.equippedArtifacts.generalSlot) {
        // Clean up any remaining generalSlot references
        delete gameState.equippedArtifacts.generalSlot;
        console.log('Cleaned up legacy generalSlot reference');
    }

    // Migrate slot property in artifact objects from generalSlot to utilitySlot
    let artifactsMigrated = false;
    if (gameState.artifacts && gameState.artifacts.length > 0) {
        gameState.artifacts.forEach(artifact => {
            if (artifact.slot === 'generalSlot') {
                artifact.slot = 'utilitySlot';
                artifactsMigrated = true;
            }
        });
    }

    // Initialize hired contractors array if it doesn't exist (but don't reset reservedContractors!)
    if (!gameState.currentRaid.hiredContractors) {
        gameState.currentRaid.hiredContractors = [];
    }

    // Restore active character based on activeCharacterId
    if (gameState.activeCharacterId && gameState.characters.length > 0) {
        const activeChar = gameState.characters.find(c => c.id === gameState.activeCharacterId);

        if (activeChar) {
            // Set the active character
            gameState.character = { ...activeChar };

        } else {
            // activeCharacterId doesn't match any character, reset to first available
            const availableChars = gameState.characters.filter(c => !c.isMarketplace);
            if (availableChars.length > 0) {
                gameState.character = { ...availableChars[0] };
                gameState.activeCharacterId = availableChars[0].id;
            } else {
                // No characters available, reset character state
                gameState.character = null;
                gameState.activeCharacterId = null;
            }
        }
    } else if (gameState.character) {
        // Legacy character loading - display if character exists
        gameState.activeCharacterId = gameState.character.id;
    }

    // Migrations applied - stamp the live state to the current schema + build.
    if (savedVersion !== SAVE_SCHEMA_VERSION) {
        console.log(`Migrated save from schema v${savedVersion} (build ${savedGameVersion || 'unknown'}) to v${SAVE_SCHEMA_VERSION}`);
    }
    gameState.schemaVersion = SAVE_SCHEMA_VERSION;
    gameState.gameVersion = GAME_VERSION;
}

/**
 * May the active character open the raid screen?
 *
 * A raid belongs to the character that started it, so a second character
 * cannot walk into someone else's run. Pure gameState logic — it lived in the
 * DOM-coupled game.js, which put it out of reach of the layer registry that
 * needs it to gate Field's active task. game.js re-exports it.
 */
export function canAccessRaid() {
    if (!gameState.character) return false;

    // If no raid in progress, anyone can access
    if (!gameState.currentRaid.raidOwnerId) return true;

    // If there's an active raid, only the owner can access
    return gameState.currentRaid.raidOwnerId === gameState.activeCharacterId;
}
